/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/custom/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Bell } from 'lucide-react'
import supabase from '@/supabaseClient'
import { useNavigate } from 'react-router-dom'
// @ts-ignore
import { LoginResponse } from '@/@types/auth'
import { getFirstCharacters } from '@/hooks/common/common-functions'
import useLocalStorage, {
  useLocalStorageString,
} from '@/hooks/use-local-storage'
import { AccountType } from '@/pages/auth/components/user-auth-form'

export function UserNav() {
  const navigate = useNavigate()
  const [userInfo] = useLocalStorage<LoginResponse | undefined>({
    key: 'vbAuth',
    defaultValue: undefined,
  })
  const accountType = useLocalStorageString('accountType') as AccountType
  const data = userInfo?.data ?? userInfo

  const handleLogout = async () => {
    try {
      // Step 1: OneSignal cleanup
      try {
        const OneSignal = (window as any).OneSignal;
        if (OneSignal && OneSignal.logout) {
          await OneSignal.logout();
        }
      } catch (error) {
        console.error('OneSignal logout error:', error);
      }
  
      // Step 2: Preserve device-specific settings BEFORE clearing everything
      const deviceSpecificData = {
        // PWA install prompt dismissal (device-specific, should persist)
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        
        // Device capabilities (should persist)
        is_ios_pwa: localStorage.getItem('is_ios_pwa'),
        
        // Any other device-specific settings you want to preserve
        // device_id: localStorage.getItem('device_id'),
        // app_version: localStorage.getItem('app_version'),
      };
      
      // Step 3: Clear ALL storage (including all OneSignal data)
      localStorage.clear();
      sessionStorage.clear();
  
      // Step 4: Restore device-specific settings that should persist across logins
      Object.entries(deviceSpecificData).forEach(([key, value]) => {
        if (value !== null) {
          // Map back to original localStorage key names
          const keyMap: Record<string, string> = {
            pwa_dismissed: 'pwa_install_prompt_dismissed',
            pwa_timestamp: 'pwa_dismissal_timestamp',
            is_ios_pwa: 'is_ios_pwa'
          };
          localStorage.setItem(keyMap[key] || key, value);
        }
      });
      
      // Step 5: Supabase logout
      await supabase.auth.signOut();
      
      // Step 6: Navigate to login
      navigate('/login');
      
    } catch (error) {
      console.error('Error logging out:', error);
      
      // Fallback - preserve device settings even if main logout fails
      const deviceData = {
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        is_ios_pwa: localStorage.getItem('is_ios_pwa')
      };
      
      localStorage.clear();
      sessionStorage.clear();
      
      // Restore device-specific data
      if (deviceData.pwa_dismissed) {
        localStorage.setItem('pwa_install_prompt_dismissed', deviceData.pwa_dismissed);
      }
      if (deviceData.pwa_timestamp) {
        localStorage.setItem('pwa_dismissal_timestamp', deviceData.pwa_timestamp);
      }
      if (deviceData.is_ios_pwa) {
        localStorage.setItem('is_ios_pwa', deviceData.is_ios_pwa);
      }
      
      navigate('/login');
    }
  }

  const getProfilePath = () => {
    switch (accountType) {
      case AccountType.business:
      case AccountType.business_admin:
        return '/settings'

      case AccountType.client:
      case AccountType.app_client:
        return '/client-portal/settings'

      case AccountType.business_team_leader:
      case AccountType.team_leader:
      case AccountType.staff_team_leader:
        return '/team-leader/settings'

      case AccountType.business_operations_managers:
      case AccountType.business_operations_manager:
      case AccountType.staff_operations_manager:
      case AccountType.operations_manager:
        return '/operations-manager/settings'
      default:
        return '/profile'
    }
  }

  const getConfigPath = () => {
    switch (accountType) {
      case AccountType.business:
      case AccountType.business_admin:
        return '/configuration/alerts'
      
      default:
        return '/configuration/alerts'
    }
  }

  const getNotificationPath = () => {
    switch (accountType) {
      case AccountType.business:
      case AccountType.business_admin:
        return '/notifications'

      case AccountType.client:
      case AccountType.app_client:
        return '/client-portal/notifications'

      case AccountType.business_team_leader:
      case AccountType.team_leader:
      case AccountType.staff_team_leader:
        return '/team-leader/notifications'

      case AccountType.business_operations_managers:
      case AccountType.business_operations_manager:
      case AccountType.staff_operations_manager:
      case AccountType.operations_manager:
        return '/operations-manager/notifications'
      
      default:
        return '/notifications'
    }
  }

  const renderMenuContent = () => (
    <DropdownMenuContent className='w-56' align='end' forceMount>
      <DropdownMenuLabel className='font-normal'>
        <div className='flex flex-col space-y-1'>
          <p className='text-sm font-medium leading-none'>{data?.user?.name}</p>
          <p className='truncate text-xs leading-none text-muted-foreground'>
            {data?.user?.email}
          </p>
          <p className='text-xs capitalize leading-none text-muted-foreground'>
            {accountType.replace('business_', '')}
          </p>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem onClick={() => navigate(getProfilePath())}>
          Profile
          <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
        </DropdownMenuItem>

        {(accountType === AccountType.business || 
          accountType === AccountType.business_admin) && (
            <DropdownMenuItem onClick={() => navigate(getConfigPath())}>
              Configurations
              <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
            </DropdownMenuItem>
          )}

        <DropdownMenuItem
          className='cursor-pointer'
          onClick={() => navigate(getNotificationPath())}
        >
          Notifications
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={handleLogout}>
        Log out
        <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
      </DropdownMenuItem>
    </DropdownMenuContent>
  )

  return (
    <div className='flex items-center space-x-2'>
      <Button
        variant='ghost'
        className='relative h-8 w-8 p-0'
        onClick={() => navigate(getNotificationPath())}
      >
        <Bell className='h-5 w-5' />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
            <Avatar className='h-8 w-8'>
              <AvatarImage src='/avatars/01.png' alt='@user' />
              <AvatarFallback>
                {getFirstCharacters(data?.user?.name)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        {renderMenuContent()}
      </DropdownMenu>
    </div>
  )
}